import { useEffect } from "react";
import MiamiHeroSection from "./components/HeroSection";
import MiamiStickyNav from "./components/StickyNav";
import MiamiProcessingTimeSection from "./components/ProcessingTimeSection";
import MiamiServicesSection from "./components/ServicesSection";
import MiamiLocalAreaSection from "./components/LocalAreaSection";
import MiamiTravelTimingSection from "./components/TravelTimingSection";
import MiamiDestinationsSection from "./components/DestinationsSection";
import MiamiTestimonialsSection from "./components/TestimonialsSection";
import MiamiAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import MiamiComparisonSection from "./components/ComparisonSection";
import MiamiMistakesSection from "./components/MistakesSection";
import MiamiHowItWorksSection from "./components/HowItWorksSection";
import MiamiFAQSection, { faqs } from "./components/FAQSection";
import MiamiHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import MiamiFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function MiamiPage() {
  useEffect(() => {
    document.title = "Emergency Passport Miami, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/miami/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Miami, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Miami-Dade County travelers, cruise passengers, and international business travelers.");
    setMeta("geo.placename", "Miami, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "25.7617;-80.1918");
    setMeta("ICBM", "25.7617, -80.1918");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Miami Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Miami — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Miami and Miami-Dade County travelers, cruise passengers, and international business travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Miami",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Miami",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <MiamiHeroSection />
      <MiamiStickyNav />
      <MiamiProcessingTimeSection />
      <MiamiServicesSection />
      <MiamiLocalAreaSection />
      <MiamiTravelTimingSection />
      <MiamiDestinationsSection />
      <MiamiTestimonialsSection />
      <MiamiAcceptanceFacilitiesSection />
      <MiamiComparisonSection />
      <MiamiMistakesSection />
      <MiamiHowItWorksSection />
      <MiamiFAQSection />
      <MiamiHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/miami" state="FL" />
      <MiamiFinalCTA />
    </div>
  );
}